package com.gastoncantera.intercom.config

import kotlinx.serialization.Serializable

// -------- DTOs de parseo (claves dinámicas: consorcio, torre, UID) --------

/** Unidad de un consorcio (torre, o el consorcio mismo cuando no hay torres). */
@Serializable
data class UnitDto(
    val pisos: Int? = null,
    val departamentos: String? = null,
    val usuarios: Map<String, String> = emptyMap(), // uid -> "3-B,14-B"
)

@Serializable
data class ConsorcioDto(
    val portero: String? = null, // uid del portero
    val pin: String? = null, // MD5 del PIN de logout (hold en título; sin pin = confirmación)
    val ubicacion: String? = null, // "lat,lng,radio_m" (gate web; sin radio = QR off en ese consorcio)
    val torres: Map<String, UnitDto>? = null, // null => usar campos root
    val pisos: Int? = null,
    val departamentos: String? = null,
    val usuarios: Map<String, String> = emptyMap(),
)

// -------- Dominio normalizado --------

data class AppConfig(val consorcios: List<Consorcio>)

data class Consorcio(
    val nombre: String,
    val porteroUid: String?,
    val pin: String?,
    val ubicacion: String?,
    val torres: List<Torre>,
)

data class Torre(
    val nombre: String?, // null cuando el consorcio no tiene torres
    val pisos: Int?,
    val departamentos: String?,
    val usuarios: List<UsuarioEntry>,
)

data class UsuarioEntry(
    val uid: String,
    val deptos: List<String>, // "3-B,14-B" -> ["3-B", "14-B"]
)

// -------- Normalización DTO -> dominio --------

fun Map<String, ConsorcioDto>.toAppConfig(): AppConfig =
    AppConfig(consorcios = map { (nombre, dto) -> dto.toConsorcio(nombre) })

private fun ConsorcioDto.toConsorcio(nombre: String): Consorcio {
    val torresDom = when (val t = torres) {
        null -> listOf(
            // Sin torres: el consorcio es una única "torre" implícita con los campos root.
            Torre(
                nombre = null,
                pisos = pisos,
                departamentos = departamentos,
                usuarios = usuarios.toUsuarioEntries(),
            )
        )
        else -> t.map { (torreNombre, unit) -> unit.toTorre(torreNombre) }
    }
    return Consorcio(nombre = nombre, porteroUid = portero, pin = pin, ubicacion = ubicacion, torres = torresDom)
}

private fun UnitDto.toTorre(nombre: String): Torre =
    Torre(
        nombre = nombre,
        pisos = pisos,
        departamentos = departamentos,
        usuarios = usuarios.toUsuarioEntries(),
    )

private fun Map<String, String>.toUsuarioEntries(): List<UsuarioEntry> =
    map { (uid, deptos) ->
        UsuarioEntry(
            uid = uid,
            deptos = deptos.split(",").map { it.trim() }.filter { it.isNotEmpty() },
        )
    }

// -------- Resolución de rol por UID (case-sensitive, sin lowercase) --------

sealed interface UserRole {
    data class Portero(val consorcios: List<Consorcio>) : UserRole
    data class Usuario(val matches: List<UsuarioMatch>) : UserRole
    data object None : UserRole
}

/**
 * Identificador del "buzón" de llamada de un depto, compartido por el portero y por todos los
 * usuarios registrados en ese depto. Determinístico: portero y usuarios lo calculan igual.
 * (Firestore no permite "/" en el id de doc; "|" es seguro.)
 */
fun callIdFor(consorcio: String, torre: String?, depto: String): String =
    listOf(consorcio, torre ?: "", depto).joinToString("|")

/** Una línea del listado del usuario: un depto concreto donde está registrado. */
data class UsuarioMatch(
    val consorcio: String,
    val torre: String?,
    val depto: String,
    val callId: String,
)

/**
 * Una línea del listado del portero: un depto contactable. Un mismo depto puede tener varios
 * usuarios ([uids]); se muestra una sola entrada y la llamada va al buzón compartido ([callId]),
 * donde suenan todos y atiende el primero.
 */
data class DeptoEntry(
    val callId: String,
    val torre: String?,
    val depto: String,
    val uids: List<String>,
)

fun AppConfig.resolveRole(uid: String): UserRole {
    val porteroDe = consorcios.filter { it.porteroUid == uid }
    if (porteroDe.isNotEmpty()) return UserRole.Portero(porteroDe)

    val matches = buildList {
        consorcios.forEach { consorcio ->
            consorcio.torres.forEach { torre ->
                torre.usuarios.filter { it.uid == uid }.forEach { entry ->
                    entry.deptos.forEach { depto ->
                        add(
                            UsuarioMatch(
                                consorcio = consorcio.nombre,
                                torre = torre.nombre,
                                depto = depto,
                                callId = callIdFor(consorcio.nombre, torre.nombre, depto),
                            )
                        )
                    }
                }
            }
        }
    }
    return if (matches.isNotEmpty()) UserRole.Usuario(matches) else UserRole.None
}

/**
 * Deptos contactables de un consorcio: una entrada por depto (dedup), agrupando todos los uids
 * registrados en él.
 */
fun Consorcio.deptos(): List<DeptoEntry> {
    val byDepto = LinkedHashMap<Pair<String?, String>, MutableList<String>>()
    torres.forEach { torre ->
        torre.usuarios.forEach { entry ->
            entry.deptos.forEach { depto ->
                byDepto.getOrPut(torre.nombre to depto) { mutableListOf() }.add(entry.uid)
            }
        }
    }
    return byDepto.map { (key, uids) ->
        val (torre, depto) = key
        DeptoEntry(
            callId = callIdFor(nombre, torre, depto),
            torre = torre,
            depto = depto,
            uids = uids.distinct(),
        )
    }
}

// -------- Render tipo edificio (grilla pisos x departamentos) --------

/** Una celda del edificio: "14-B". [configurado] == hay algún usuario asignado (uids no vacío). */
data class Departamento(
    val nombre: String,
    val callId: String,
    val uids: List<String>,
) {
    val configurado: Boolean get() = uids.isNotEmpty()
}

/** Un piso con sus departamentos, en orden alfabético de izquierda a derecha. */
data class Piso(val numero: Int, val departamentos: List<Departamento>)

/**
 * Un edificio a renderizar: una torre (o el consorcio si no tiene torres). [pisos] viene ordenado
 * del más bajo al más alto (el piso 1 va arriba en pantalla).
 */
data class Edificio(
    val consorcio: String,
    val torre: String?,
    val casa: Boolean,
    val cantidadPisos: Int?,
    val departamentosRango: String?,
    val pisos: List<Piso>,
)

/**
 * Expande un rango "A-F" a [A, B, C, D, E, F]. Si no es un rango de una letra, devuelve la lista
 * literal separada por coma como fallback.
 */
fun parseDepartamentos(rango: String?): List<String> {
    if (rango.isNullOrBlank()) return emptyList()
    val partes = rango.split("-").map { it.trim() }.filter { it.isNotEmpty() }
    if (partes.size == 2 && partes[0].length == 1 && partes[1].length == 1) {
        val desde = partes[0][0]
        val hasta = partes[1][0]
        if (desde <= hasta) return (desde..hasta).map { it.toString() }
    }
    return rango.split(",").map { it.trim() }.filter { it.isNotEmpty() }
}

/** Edificios del consorcio: uno por torre, o uno solo (raíz) si no tiene torres. */
fun Consorcio.edificios(): List<Edificio> = torres.map { torre ->
    val letras = parseDepartamentos(torre.departamentos)
    val uidsPorDepto = LinkedHashMap<String, MutableList<String>>()
    torre.usuarios.forEach { entry ->
        entry.deptos.forEach { depto ->
            uidsPorDepto.getOrPut(depto) { mutableListOf() }.add(entry.uid)
        }
    }

    val casa = torre.pisos == null && torre.departamentos.isNullOrBlank()
    val pisosList = when (val n = torre.pisos) {
        null -> if (casa && uidsPorDepto.isNotEmpty()) {
            listOf(
                Piso(
                    numero = 0,
                    departamentos = uidsPorDepto.map { (nombre, uids) ->
                        Departamento(
                            nombre = nombre,
                            callId = callIdFor(consorcio = this.nombre, torre = torre.nombre, depto = nombre),
                            uids = uids.distinct(),
                        )
                    },
                )
            )
        } else {
            emptyList()
        }
        else -> (1..n).map { piso ->
            Piso(
                numero = piso,
                departamentos = letras.map { letra ->
                    val nombre = "$piso-$letra"
                    Departamento(
                        nombre = nombre,
                        callId = callIdFor(consorcio = this.nombre, torre = torre.nombre, depto = nombre),
                        uids = (uidsPorDepto[nombre] ?: emptyList()).distinct(),
                    )
                },
            )
        }
    }

    Edificio(
        consorcio = nombre,
        torre = torre.nombre,
        casa = casa,
        cantidadPisos = torre.pisos,
        departamentosRango = torre.departamentos,
        pisos = pisosList,
    )
}
